import React, { useEffect, useState } from "react";
import api, { apiErr, fileUrl } from "@/lib/api";
import { money } from "@/context/StoreContext";
import { AdminCard, Badge } from "./AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, Trash2, Pencil, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";

const SIZES = ["XS", "S", "M", "L", "XL"];
const empty = { name: "", description: "", category: "knitted-tops", collection: "", price: "", mrp: "", gst: 5,
  sizes: ["S", "M", "L"], colors: [{ name: "Blush Pink", hex: "#F8D7DA" }], images: [], video: "", tags: "",
  is_new: false, is_best_seller: false, non_returnable: false, seo: { title: "", description: "" }, stockDefault: 12 };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [cols, setCols] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); api.get("/collections").then((r) => setCols(r.data)); }, []);

  const dup = async (id) => { await api.post(`/admin/products/${id}/duplicate`); toast("Duplicated"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); toast("Deleted"); load(); };
  const archive = async (p) => { await api.put(`/admin/products/${p.id}`, { status: p.status === "active" ? "archived" : "active" }); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif text-3xl">Products</h1>
        <button data-testid="add-product-btn" onClick={() => setEditing(empty)} className="flex items-center gap-2 bg-sharoa-rose text-white text-xs uppercase tracking-widest px-4 py-2.5"><Plus size={15} /> Add Product</button>
      </div>
      <AdminCard className="overflow-x-auto p-0">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-sharoa-ivory text-left text-xs uppercase text-sharoa-muted"><tr>{["Product", "Category", "Price", "Stock", "Status", ""].map((h) => <th key={h} className="py-3 px-4">{h}</th>)}</tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-sharoa-line">
                <td className="py-3 px-4 flex items-center gap-2"><img src={p.images?.[0]} alt="" className="w-10 h-12 object-cover" />{p.name}</td>
                <td className="py-3 px-4 capitalize">{p.category?.replace(/-/g, " ")}</td>
                <td className="py-3 px-4 font-mono">{money(p.price)}</td>
                <td className="py-3 px-4">{p.stock}</td>
                <td className="py-3 px-4"><Badge tone={p.status === "active" ? "green" : "gray"}>{p.status}</Badge></td>
                <td className="py-3 px-4">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ ...p, tags: (p.tags || []).join(", "), stockDefault: 12 })} title="Edit"><Pencil size={15} /></button>
                    <button onClick={() => dup(p.id)} title="Duplicate"><Copy size={15} /></button>
                    <button onClick={() => archive(p)} title="Archive" className="text-xs">{p.status === "active" ? "Archive" : "Restore"}</button>
                    <button onClick={() => del(p.id)} title="Delete"><Trash2 size={15} className="text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminCard>
      {editing && <ProductForm product={editing} cats={cats} cols={cols} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProductForm({ product, cats, cols, onClose, onSaved }) {
  const [f, setF] = useState(product);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(fileUrl(r.data.url));
      }
      set("images", [...(f.images || []), ...urls]);
    } catch (er) { toast.error(apiErr(er)); }
    setUploading(false);
  };

  const buildVariants = () => {
    const existing = product.variants || [];
    const price = Number(f.price), mrp = Number(f.mrp);
    const overrides = f.variantStock || {};
    const vs = [];
    f.colors.forEach((c) => f.sizes.forEach((s) => {
      const prev = existing.find((v) => v.color === c.name && v.size === s);
      const key = `${c.name}|${s}`;
      const base = prev ? { ...prev, price, mrp, sale_price: price, return_eligible: !f.non_returnable }
        : { sku: `SHR-${Math.random().toString(36).slice(2, 8).toUpperCase()}`, size: s, color: c.name, color_hex: c.hex,
          price, mrp, sale_price: price, stock: Number(f.stockDefault) || 0, reserved: 0, sold: 0, weight: 300, available: true, return_eligible: !f.non_returnable };
      if (overrides[key] !== undefined && overrides[key] !== "") base.stock = Number(overrides[key]);
      base.available = base.stock > 0;
      vs.push(base);
    }));
    return vs;
  };
  const previewVariants = buildVariants();
  const setVariantStock = (key, val) => set("variantStock", { ...(f.variantStock || {}), [key]: val });
  const applyDefaultToAll = () => { const m = {}; previewVariants.forEach((v) => { m[`${v.color}|${v.size}`] = Number(f.stockDefault) || 0; }); set("variantStock", m); };

  const save = async () => {
    if (!f.name || !f.price) { toast.error("Name and price required"); return; }
    const payload = { name: f.name, description: f.description, category: f.category, collection: f.collection || null,
      price: Number(f.price), mrp: Number(f.mrp) || Number(f.price), sale_price: Number(f.price), gst: Number(f.gst) || 5,
      sizes: f.sizes, colors: f.colors, images: f.images, video: f.video || null,
      tags: (f.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
      is_new: f.is_new, is_best_seller: f.is_best_seller, on_sale: Number(f.price) < Number(f.mrp),
      non_returnable: f.non_returnable, return_eligible: !f.non_returnable, variants: buildVariants(),
      seo: f.seo, status: "active", rating: f.rating || 4.6, review_count: f.review_count || 0, sold_count: f.sold_count || 0 };
    try {
      if (product.id) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast("Saved ♡"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const addColor = () => set("colors", [...f.colors, { name: "New", hex: "#D88A8A" }]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-h-[92vh] overflow-y-auto max-w-2xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{product.id ? "Edit" : "Add"} Product</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="pf-name" placeholder="Product Name" value={f.name} onChange={(e) => set("name", e.target.value)} className="w-full border border-sharoa-line px-3 py-2.5 text-sm" />
          <textarea placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} className="w-full border border-sharoa-line px-3 py-2.5 text-sm h-20" />
          <div className="grid grid-cols-2 gap-2">
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className="border border-sharoa-line px-3 py-2.5 text-sm">{cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}</select>
            <select value={f.collection} onChange={(e) => set("collection", e.target.value)} className="border border-sharoa-line px-3 py-2.5 text-sm"><option value="">No Collection</option>{cols.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}</select>
            <input data-testid="pf-price" type="number" placeholder="Sale Price ₹" value={f.price} onChange={(e) => set("price", e.target.value)} className="border border-sharoa-line px-3 py-2.5 text-sm" />
            <input type="number" placeholder="MRP ₹" value={f.mrp} onChange={(e) => set("mrp", e.target.value)} className="border border-sharoa-line px-3 py-2.5 text-sm" />
            <input type="number" placeholder="GST %" value={f.gst} onChange={(e) => set("gst", e.target.value)} className="border border-sharoa-line px-3 py-2.5 text-sm" />
            <input type="number" placeholder="Default stock / variant" value={f.stockDefault} onChange={(e) => set("stockDefault", e.target.value)} className="border border-sharoa-line px-3 py-2.5 text-sm" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-sharoa-muted mb-1">Sizes</p>
            <div className="flex gap-2">{SIZES.map((s) => <button key={s} type="button" onClick={() => set("sizes", f.sizes.includes(s) ? f.sizes.filter((x) => x !== s) : [...f.sizes, s])} className={`w-9 h-9 text-sm border ${f.sizes.includes(s) ? "bg-sharoa-charcoal text-white" : "border-sharoa-line"}`}>{s}</button>)}</div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-sharoa-muted mb-1">Colours</p>
            {f.colors.map((c, i) => (
              <div key={i} className="flex gap-2 mb-1">
                <input value={c.name} onChange={(e) => { const cp = [...f.colors]; cp[i] = { ...c, name: e.target.value }; set("colors", cp); }} className="flex-1 border border-sharoa-line px-2 py-1.5 text-sm" />
                <input type="color" value={c.hex} onChange={(e) => { const cp = [...f.colors]; cp[i] = { ...c, hex: e.target.value }; set("colors", cp); }} className="w-10 h-9 border border-sharoa-line" />
                <button onClick={() => set("colors", f.colors.filter((_, j) => j !== i))}><X size={16} /></button>
              </div>
            ))}
            <button onClick={addColor} className="text-xs text-sharoa-rose flex items-center gap-1"><Plus size={13} /> Add Colour</button>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-sharoa-muted mb-1">Images</p>
            <div className="flex flex-wrap gap-2 mb-2">{(f.images || []).map((src, i) => (
              <div key={i} className="relative"><img src={src} alt="" className="w-16 h-20 object-cover" /><button onClick={() => set("images", f.images.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-white rounded-full"><X size={14} /></button></div>
            ))}</div>
            <label className="inline-flex items-center gap-2 border border-sharoa-line px-3 py-2 text-xs cursor-pointer"><Upload size={14} /> {uploading ? "Uploading…" : "Upload Images"}<input type="file" accept="image/*" multiple hidden onChange={upload} /></label>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><p className="text-xs uppercase tracking-widest text-sharoa-muted">Stock per variant</p>
              <button type="button" data-testid="pf-apply-default-stock" onClick={applyDefaultToAll} className="text-xs text-sharoa-rose">Apply default ({f.stockDefault || 0}) to all</button></div>
            <div className="border border-sharoa-line divide-y divide-sharoa-line max-h-48 overflow-auto" data-testid="pf-variant-table">
              {previewVariants.map((v) => { const key = `${v.color}|${v.size}`; return (
                <div key={key} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                  <span className="flex-1">{v.color} / {v.size}</span><span className="font-mono text-xs text-sharoa-muted">{v.sku}</span>
                  <input data-testid={`pf-stock-${v.color.replace(/\s/g, "-")}-${v.size}`} type="number" min="0" value={f.variantStock?.[key] ?? v.stock} onChange={(e) => setVariantStock(key, e.target.value)} className="w-20 border border-sharoa-line px-2 py-1 text-sm" />
                </div>); })}
              {previewVariants.length === 0 && <p className="p-3 text-xs text-sharoa-muted">Pick at least one size and colour.</p>}
            </div>
          </div>
          <input placeholder="Tags (comma separated)" value={f.tags} onChange={(e) => set("tags", e.target.value)} className="w-full border border-sharoa-line px-3 py-2.5 text-sm" />
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_new} onChange={(e) => set("is_new", e.target.checked)} className="accent-sharoa-rose" /> New Arrival</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_best_seller} onChange={(e) => set("is_best_seller", e.target.checked)} className="accent-sharoa-rose" /> Best Seller</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.non_returnable} onChange={(e) => set("non_returnable", e.target.checked)} className="accent-sharoa-rose" /> Non-Returnable (Final Sale)</label>
          </div>
          <button data-testid="pf-save" onClick={save} className="w-full bg-sharoa-rose text-white text-xs uppercase tracking-widest py-3">Save Product</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
